// 焼き込み静的メッシュ（Union=CSG の実形状など）の描画。
//
// StaticMeshDescriptor のワールド座標三角形を MeshStandardMaterial で描く。
// 周囲の Part（壁・スロープ）と見た目を揃えるため、素材の模様テクスチャ
// （materialTextures）を box 投影 UV（面の支配軸へ投影、4 studs = 1 タイル）で
// タイルする。CSG 由来でエッジが立った形状のため法線は面単位（フラット）。
// 物理側は同じ三角形から trimesh コライダーを作るので、見た目と当たり判定が
// 一致する。
import * as THREE from 'three'

import { MATERIAL_TILE_STUDS, createMaterialTexture } from './materialTextures'
import { StaticMeshDescriptor } from '../types'

export interface StaticMeshView {
  readonly group: THREE.Group
  dispose(): void
}

/**
 * インデックスを展開（頂点を三角形ごとに複製）し、面ごとの UV を box 投影で
 * 与えたジオメトリを作る。頂点を面間で共有すると、支配軸の異なる面に同一
 * 頂点 UV を割り当てられないため展開する（凸ハル由来の小規模メッシュ前提）。
 */
const buildGeometry = (
  descriptor: StaticMeshDescriptor,
): THREE.BufferGeometry => {
  const { positions, indices } = descriptor
  const vertexCount = indices.length
  const expanded = new Float32Array(vertexCount * 3)
  const uvs = new Float32Array(vertexCount * 2)
  const a = new THREE.Vector3()
  const b = new THREE.Vector3()
  const c = new THREE.Vector3()
  const ab = new THREE.Vector3()
  const ac = new THREE.Vector3()
  const normal = new THREE.Vector3()
  for (let tri = 0; tri < indices.length / 3; tri += 1) {
    const ia = indices[tri * 3]
    const ib = indices[tri * 3 + 1]
    const ic = indices[tri * 3 + 2]
    a.fromArray(positions, ia * 3)
    b.fromArray(positions, ib * 3)
    c.fromArray(positions, ic * 3)
    ab.subVectors(b, a)
    ac.subVectors(c, a)
    normal.crossVectors(ab, ac)
    // 面法線の支配軸に直交する平面へ投影して UV にする（Part の box マッピング
    // 相当）。スケールは Part と同じ「MATERIAL_TILE_STUDS studs = 1 タイル」。
    const ax = Math.abs(normal.x)
    const ay = Math.abs(normal.y)
    const az = Math.abs(normal.z)
    for (const [corner, vertex] of [a, b, c].entries()) {
      const base = (tri * 3 + corner) * 3
      expanded[base] = vertex.x
      expanded[base + 1] = vertex.y
      expanded[base + 2] = vertex.z
      let u: number
      let v: number
      if (ay >= ax && ay >= az) {
        u = vertex.x
        v = vertex.z
      } else if (ax >= az) {
        u = vertex.z
        v = vertex.y
      } else {
        u = vertex.x
        v = vertex.y
      }
      uvs[(tri * 3 + corner) * 2] = u / MATERIAL_TILE_STUDS
      uvs[(tri * 3 + corner) * 2 + 1] = v / MATERIAL_TILE_STUDS
    }
  }
  const geometry = new THREE.BufferGeometry()
  geometry.setAttribute('position', new THREE.BufferAttribute(expanded, 3))
  geometry.setAttribute('uv', new THREE.BufferAttribute(uvs, 2))
  // 頂点は面間で非共有なので、これで面単位（フラット）の法線になる。
  geometry.computeVertexNormals()
  return geometry
}

/** 静的メッシュ群の描画ビューを作る。空配列なら null。 */
export const createStaticMeshView = (
  meshes: StaticMeshDescriptor[],
): StaticMeshView | null => {
  if (meshes.length === 0) {
    return null
  }
  const group = new THREE.Group()
  // 模様テクスチャは material.dispose では解放されないため個別に保持する。
  const patternTextures: THREE.Texture[] = []
  for (const descriptor of meshes) {
    const material = new THREE.MeshStandardMaterial({
      color: new THREE.Color(descriptor.color),
      roughness: 0.9,
      metalness: 0,
    })
    // 周囲の Part と同じ素材模様をタイルする（色は乗算で同系に揃う）。
    const patternTexture =
      descriptor.material ? createMaterialTexture(descriptor.material) : null
    if (patternTexture) {
      patternTextures.push(patternTexture)
      material.map = patternTexture
    }
    group.add(new THREE.Mesh(buildGeometry(descriptor), material))
  }

  return {
    group,
    dispose: (): void => {
      for (const child of group.children) {
        if (child instanceof THREE.Mesh) {
          child.geometry.dispose()
          const material = child.material
          if (Array.isArray(material)) {
            for (const m of material) {
              m.dispose()
            }
          } else {
            material.dispose()
          }
        }
      }
      for (const texture of patternTextures) {
        texture.dispose()
      }
      patternTextures.length = 0
      group.clear()
    },
  }
}
